<template>
  <form
    class="password-recovery-form"
    @submit.prevent
    @keyup.enter="onEnter"
  >
    <!-- step1: input phone or email -->
    <template v-if="!isShowCode">
      <UiAlert
        v-if="timeOutError"
        transparent
        type="error"
        class="password-recovery-form__alert"
      >
        {{ $tc('regAuthForgot.passwordRecovery.manyRequests1') }}
        <br />
        {{ $tc('regAuthForgot.passwordRecovery.manyRequests2') }}
      </UiAlert>
      <BaseInputPhone
        v-if="isActiveTabPhone"
        ref="phoneRef"
        key="phone"
        v-model="phone"
        focus
        :error-messages="errorMessages.phone"
        @input="errorMessages.phone = []"
        @onValidate="v => isInternalValidPhone = v"
      />
      <BaseInputEmail
        v-else
        ref="emailRef"
        key="email"
        v-model="email"
        :placeholder="$tc('regAuthForgot.placeholder.emailHere')"
        focus
        :error-messages="errorMessages.email"
        @input="errorMessages.email = []"
      />
      <UiBtn
        color="primary"
        full-width
        size="l"
        class="password-recovery-form__submit"
        :text="$tc('regAuthForgot.passwordRecovery.continue')"
        @click="onGetCode"
      />
    </template>
    <!-- step2: input code from sms/email -->
    <template v-else>
      <UiText
        v-if="!isInitEmailCodeByQuery"
        font="body-h2"
        color="typography-title01"
        align-center
        :text="$tc('forms.passwordRecovery.successInfo.title')"
      />
      <div v-if="!isInitEmailCodeByQuery" class="password-recovery-form__text">
        <UiText
          font="body-text-small"
          align-center
          color="typography-body01"
          :text="$tc('regAuthForgot.passwordRecovery.successCodeSent1')"
        />
        <div class="password-recovery-form__text-middle">
          <UiText
            font="body-text-small"
            align-center
            color="typography-title01"
            :text="isActiveTabPhone ? `${phone.code}${phone.tel}` : email"
          />
          <UiBtn
            color="transparent"
            icon="profile/edit"
            size="s"
            @click="onEdit"
          />
        </div>
        <UiText
          font="body-text-small"
          align-center
          color="typography-body01"
          :text="$tc('regAuthForgot.passwordRecovery.successCodeSent2')"
        />
      </div>
      <BaseInputCode
        ref="BaseInputCodeRef"
        v-model="code"
        class="password-recovery-form__one-time-code"
        :regexp="REG_NOT_LOW_LETTERS_NUMBERS"
        :has-error="!!errorMessages.oneTimeCode.length"
        focus
        @input="onInputCode"
        @filled="onSubmitPasswordRecovery"
      />
      <UiAlert
        v-if="timeOutError"
        transparent
        type="error"
        class="password-recovery-form__alert-time-out"
      >
        {{ $tc('regAuthForgot.passwordRecovery.manyRequests1') }}
        <br />
        {{ $tc('regAuthForgot.passwordRecovery.manyRequests2') }}
      </UiAlert>
      <div v-if="!isInitEmailCodeByQuery" class="password-recovery-form__repeat-send">
        <template
          v-if="!!countdown.intervalId || isSubmitted.code || isDisabledSendCode"
        >
          <UiText
            tag="span"
            font="body-text-small"
            color="typography-body01"
            :text="$tc('forms.passwordRecovery.sendCodeAgainAfter')"
          />
          <UiLink
            tag="span"
            color="green"
            bold
            :text="secondsLeft"
          />
        </template>
        <UiLink
          v-else-if="!timeOutError"
          tag="a"
          color="green"
          bold
          :text="$tc('forms.passwordRecovery.sendCodeAgain')"
          @click="onGetCode"
        />
      </div>
      <UiBtn
        :disabled="isSubmitted.form || !!errorMessages.oneTimeCode.length"
        :loading="isSubmitted.form"
        color="primary"
        full-width
        size="l"
        data-id="password-recovery-btn"
        class="password-recovery-form__recover-button"
        :text="$tc('regAuthForgot.passwordRecovery.button')"
        @click="onSubmitPasswordRecovery"
      />
      <BaseSupportButton
        size="l"
        full-width
        :text="$tc('supportBtn')"
        class="password-recovery-form__support"
      />
    </template>
    <!-- step3: input new password -->
    <ModalRecaptcha v-model="isShowRecaptchaModal" @recaptcha-success="onRecaptchaSuccess" />
  </form>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { validationMixin } from 'vuelidate'
import { required, email, minLength } from 'vuelidate/lib/validators'
import {
  phoneValidationErrorMessage,
  emailValidationErrorMessage
} from '@/util/inputValidatonErrorMessage'
import BaseInputCode from '~/components/Base/Input/Code'
import BaseInputPhone from '~/components/Base/Input/Phone'
import BaseSupportButton from '@/components/Base/SupportButton'
import ModalRecaptcha from '~/components/Modal/ModalRecaptcha'
import { REG_NOT_LOW_LETTERS_NUMBERS } from '~/constants/regexp'
import { INVALID_CAPTCHA, NEED_CAPTCHA } from '~/services/_api/helpers/_constants'
import BaseInputEmail from '~/components/Base/Input/Email'

const TIMEOUT_TO_NEXT_CODE_REQUEST = 30 // seconds

export default {
  name: 'PasswordRecoveryForm',
  components: {
    BaseInputCode,
    ModalRecaptcha,
    BaseInputPhone,
    BaseInputEmail,
    BaseSupportButton,
  },
  mixins: [validationMixin],
  validations() {
    if (this.isShowCode) {
      return {
        code: { required, minLength: minLength(6) },
      }
    }
    else if (this.isActiveTabEmail) {
      return {
        email: { required, email },
      }
    }
    else {
      return {
        phone: {
          tel: {
            required,
            custom() {
              return this.isInternalValidPhone
            },
          },
        },
      }
    }
  },
  data() {
    return {
      phone: { tel: '', code: '+38' },
      email: '',
      code: '',
      isShowCode: false,
      countdown: {
        current: TIMEOUT_TO_NEXT_CODE_REQUEST,
        initial: TIMEOUT_TO_NEXT_CODE_REQUEST,
        intervalId: null,
      },
      isSubmitted: {
        form: false,
        code: false,
      },
      REG_NOT_LOW_LETTERS_NUMBERS,
      /** error handle */
      errorMessages: {
        phone: [],
        email: [],
        oneTimeCode: [],
      },
      timeOutError: false,
      recaptchaToken: '',
      isShowRecaptchaModal: false,
      isInternalValidPhone: false,
      isInitEmailCodeByQuery: false,
    }
  },
  computed: {
    ...mapGetters('login', [
      'get_normalPasswordResponse',
      'get_resultPasswordRequest',
    ]),
    secondsLeft() {
      if (this.countdown.current < 10) {
        return `0:0${this.countdown.current}`
      }
      return `0:${this.countdown.current}`
    },
    isRecaptchaModalShouldBeShow() {
      return !!(
        (
          this.get_normalPasswordResponse?.errors === NEED_CAPTCHA
          || this.get_normalPasswordResponse?.errors === INVALID_CAPTCHA
        )
        && !this.recaptchaToken)
    },
    isDisabledSendCode() {
      return this.isActiveTabPhone
        ? !!this.errorMessages.phone.length
        : !!this.errorMessages.email.length
    },
    isActiveTabPhone() {
      return this.activeTab === 'phone'
    },
    isActiveTabEmail() {
      return this.activeTab === 'email'
    },
    activeTab() {
      return this.$route.query.bigModalTab
    },
    payload() {
      const res = this.isActiveTabEmail
        ? {
          email: this.email,
          type: this.activeTab,
        }
        : {
          phone_code: this.phone.code,
          phone: this.phone.tel,
          type: this.activeTab,
        }
      if (this.recaptchaToken) res.recaptchaToken = this.recaptchaToken
      return res
    },
    userExistsData() {
      return this.$store.getters['user/userExistsData']
    },
  },
  created() {
    if (process.client) {
      if (Object.keys(this.userExistsData).length) {
        this.email = this.userExistsData?.email
        this.phone = { code: this.userExistsData?.code, tel: this.userExistsData?.tel }
      }
      this.tryInitEmailCodeByQuery()
    }
  },
  beforeDestroy() {
    clearInterval(this.countdown.intervalId)
    this.$recaptcha.destroy()
  },
  methods: {
    ...mapActions('globalModal', ['open_globalModalSupport']),
    ...mapActions('login', [
      'restore_passwordRequest',
      'restore_passwordConfirm',
      'act_resultPasswordChange',
    ]),
    ...mapActions('notifyToast', ['show_notify']),
    phoneValidationErrorMessage,
    emailValidationErrorMessage,
    onEdit() {
      this.isShowCode = false
      this.code = ''
    },
    tryInitEmailCodeByQuery() {
      if (!this.$route.query.token) return
      const { token = '', ...query } = this.$route.query
      this.code = token
      this.isShowCode = true
      this.$router.replace({ query })
      this.isInitEmailCodeByQuery = true
    },
    onRecaptchaSuccess(recaptchaToken) {
      this.recaptchaToken = recaptchaToken
      this.isShowRecaptchaModal = false
    },
    onEnter() {
      this.isShowCode
        ? this.onSubmitPasswordRecovery()
        : this.onGetCode()
    },
    /** update Errors */
    updateCodeErrors() {
      // before form submit
      if (this.$v.$invalid) {
        this.errorMessages.oneTimeCode = []
        if (!this.$v?.code.required) {
          this.errorMessages.oneTimeCode.push(this.$t('inputs.required'))

          this.show_notify({
            text: this.$t('inputs.required'),
            type: 'error',
          })
          return
        }
        if (!this.$v?.code.minLength) {
          const errorText = this.isActiveTabEmail ? this.$t('forms.errors.wrongCodeEmail') : this.$t('forms.errors.wrongCodeSms')
          this.errorMessages.oneTimeCode.push(errorText)

          this.show_notify({
            text: errorText,
            type: 'error',
          })
        }
      }
    },
    updateErrors() {
      // before phone / email submit
      this.errorMessages.phone = []
      this.errorMessages.email = []
      this.errorMessages.oneTimeCode = []
      if (this.$v.$invalid) {
        if (this.$v?.phone?.$error) {
          this.errorMessages.phone = this.phoneValidationErrorMessage(this.$v.phone.tel)
        }
        if (this.$v?.email?.$error) {
          this.errorMessages.email = this.emailValidationErrorMessage(this.$v.email)
        }
      }
    },
    /** Countdown */
    startCountdown() {
      this.countdown.current = this.countdown.initial
      this.countdown.intervalId = setInterval(() => {
        if (this.countdown.current === 0) {
          this.isSubmitted.code = false
          clearInterval(this.countdown.intervalId)
          this.countdown.intervalId = null
          return
        }
        this.countdown.current -= 1
      }, 1000)
    },
    /** Queries, phone or email input */
    async onGetCode() {
      if (this.isRecaptchaModalShouldBeShow) {
        this.isShowRecaptchaModal = true
        return
      }
      if (this.isActiveTabPhone && this.errorMessages.phone.length) return
      if (this.isActiveTabEmail && this.errorMessages.email.length) return
      if (this.isSubmitted.form) return
      if (!this.isShowCode) {
        this.$v.$touch()
        this.updateErrors()
        if (this.$v.$invalid) return
      }
      this.isSubmitted.form = true
      this.isSubmitted.code = true
      this.errorMessages.code = []
      this.timeOutError = false
      try {
        const res = await this.restore_passwordRequest(this.payload)
        if (res.status) {
          if (this.isShowCode) {
            this.oneTimeCode = ''
            this.$v.code.$reset()
          }
          this.isShowCode = true
          clearInterval(this.countdown.intervalId)
          this.startCountdown()
          this.$nextTick(() => {
            this.$refs.BaseInputCodeRef?.$addFocus()
          })
          this.show_notify({
            text: this.$tc('regAuthForgot.passwordRecovery.successCodeSent3'),
            type: 'success',
          })
        }
        else {
          if (this.isRecaptchaModalShouldBeShow) {
            this.isShowRecaptchaModal = true
          }
          this.isSubmitted.code = false
          if (res.errorMessage === 'Precondition Failed') {
            this.timeOutError = true
            // this.show_notify({
            //   text: this.$tc('regAuthForgot.passwordRecovery.successCodeSent3'),
            //   type: 'error',
            // })
          }
          if (res.errors.phone === 'user_disabled') {
            this.errorMessages.phone.push(this.$t('popup.errors.userPhone'))
            return
          }
          if (res.errors.email === 'user_disabled') {
            this.errorMessages.email.push(this.$t('popup.errors.emailNotFound'))
          }
        }
      }
      catch (e) {
        this.isShowRecaptchaModal = this.isRecaptchaModalShouldBeShow
        this.isSubmitted.code = false
      }
      finally {
        this.isSubmitted.form = false
        try {
          if (this.recaptchaToken) {
            this.recaptchaToken = ''
            this.$recaptcha.reset()
          }
        }
        catch (error) {}
      }
    },
    async onSubmitPasswordRecovery() {
      if (this.isRecaptchaModalShouldBeShow) {
        this.isShowRecaptchaModal = true
      }
      if (this.isSubmitted.form) return
      this.isSubmitted.form = true
      this.$v.$touch()
      this.updateCodeErrors()
      if (this.$v.$invalid || this.errorMessages.oneTimeCode.length) {
        this.isSubmitted.form = false
        return
      }
      this.errorMessages.oneTimeCode = []
      try {
        const res = await this.restore_passwordConfirm({ restore_token: this.code })
        if (res.status) {
          await this.act_resultPasswordChange(true)
        }
        else {
          // if (res.errors === 'user_restore_token_expired') {
          if (res.errors) {
            const errorText = this.isActiveTabEmail ? this.$t('forms.errors.wrongCodeEmail') : this.$t('forms.errors.wrongCodeSms')
            this.errorMessages.oneTimeCode.push(errorText)
            
            await this.show_notify({
              text: errorText,
              type: 'error',
            })
          }
          else {
            console.error(res)
          }
        }
      }
      finally {
        this.isSubmitted.form = false
        this.isShowRecaptchaModal = this.isRecaptchaModalShouldBeShow
      }
    },
    onInputCode() {
      if (this.isSubmitted.form) this.isSubmitted.form = false
      if (this.errorMessages.oneTimeCode.length) this.errorMessages.oneTimeCode = []
    },
  },
}
</script>

<style lang="scss" src="./style.scss" scoped></style>
